import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Modal, Badge, Button, Spinner } from 'react-bootstrap';

const COLOR_OPTIONS = [
  { value: 'primary', label: 'Azul (Comercial)', hex: '#2563eb' },
  { value: 'danger', label: 'Rojo (Administrador)', hex: '#dc2626' },
  { value: 'warning', label: 'Ámbar (Supervisor)', hex: '#d97706' },
  { value: 'success', label: 'Verde (Operativo)', hex: '#16a34a' },
  { value: 'info', label: 'Celeste (Consulta)', hex: '#0891b2' },
  { value: 'dark', label: 'Oscuro (Gerencia)', hex: '#1e293b' },
  { value: 'secondary', label: 'Gris (General)', hex: '#64748b' },
];

const RolesProfilesAdmin = ({ user }) => {
  const [activeTab, setActiveTab] = useState('roles');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [roles, setRoles] = useState([]);
  const [operadores, setOperadores] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [catalogoModulos, setCatalogoModulos] = useState([]);
  const [catalogoPermisos, setCatalogoPermisos] = useState([]);

  const parseBool = (val) => {
    if (Array.isArray(val)) {
      if (val.length === 0) return false;
      val = val[0];
    }
    if (val === true || val === 1 || val === -1 || val === '1' || val === '-1' || val === 'true' || val === 't' || val === 'v' || val === 'verdadero' || val === 'si' || val === 'sí') return true;
    return false;
  };

  const activeCompanyId = user?.empresa_activa?.c_nuges ?? user?.empresa_activa?.id;
  const currentCompanyObj = empresas.find(e => String(e.c_nuges ?? e.id) === String(activeCompanyId)) || user?.empresa_activa;
  const isWebEnabled = parseBool(currentCompanyObj?.l_sitien);

  const visibleModulos = useMemo(() => {
    if (isWebEnabled) return catalogoModulos;
    return catalogoModulos.filter(m => m.id !== 'configuracion-web' && m.id !== 'clientes-tienda');
  }, [catalogoModulos, isWebEnabled]);

  // Filtros de operadores
  const [searchOp, setSearchOp] = useState('');
  const [filterRolId, setFilterRolId] = useState('');

  // Modal de Crear / Editar Rol
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [roleForm, setRoleForm] = useState({
    id: null,
    nombre: '',
    descripcion: '',
    color: 'primary',
    es_admin: false,
    modulos: ['quoter', 'quote-viewer'],
    permisos_especiales: {
      l_enwhat: true,
    },
    sincronizar_operadores: true,
  });

  // Modal de Editar Operador
  const [showOpModal, setShowOpModal] = useState(false);
  const [opForm, setOpForm] = useState({
    c_usuar: '',
    d_nomus: '',
    d_apeus: '',
    d_email: '',
    rol_id: 0,
    l_admin: false,
    l_enwhat: false,
    empresas_permitidas: [],
  });

  const syncCurrentSessionIfNeeded = useCallback(
    (updatedOperators) => {
      if (!user?.c_usuar || !Array.isArray(updatedOperators)) return;
      const myOp = updatedOperators.find(
        (o) => String(o.c_usuar).trim().toUpperCase() === String(user.c_usuar).trim().toUpperCase()
      );
      if (!myOp) return;

      try {
        const mergeUser = (raw) => {
          if (!raw) return null;
          const parsed = JSON.parse(raw);
          return JSON.stringify({
            ...parsed,
            l_admin: myOp.es_admin_efectivo,
            rol_id: myOp.rol_id,
            rol: myOp.rol,
            rol_color: myOp.rol_color,
            modulos_permitidos: myOp.modulos_permitidos,
            permisos_especiales: myOp.permisos_efectivos,
            l_enwhat: Boolean(myOp.permisos_efectivos?.l_enwhat),
            l_modpr: Boolean(myOp.permisos_efectivos?.l_modpr),
            l_campr: Boolean(myOp.permisos_efectivos?.l_campr),
            l_ancom: Boolean(myOp.permisos_efectivos?.l_ancom),
            l_emincr: Boolean(myOp.permisos_efectivos?.l_emincr),
          });
        };
        const updatedSession = mergeUser(sessionStorage.getItem('tabUserData'));
        if (updatedSession) sessionStorage.setItem('tabUserData', updatedSession);
        const updatedLocal = mergeUser(localStorage.getItem('userData'));
        if (updatedLocal) localStorage.setItem('userData', updatedLocal);
      } catch (e) {
        console.warn('No se pudo actualizar la sesión local:', e);
      }
    },
    [user]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/roles-perfiles/', {
        params: { company: activeCompanyId },
        headers: { 'X-Company-ID': String(activeCompanyId) }
      });
      const data = response.data || {};
      setRoles(data.roles || []);
      setOperadores(data.operadores || []);
      setEmpresas(data.empresas || []);
      setCatalogoModulos(data.catalogo_modulos || []);
      setCatalogoPermisos(data.catalogo_permisos || []);
      syncCurrentSessionIfNeeded(data.operadores || []);
    } catch (error) {
      console.error('Error al cargar roles y perfiles:', error);
      Swal.fire(
        'Error',
        error.response?.data?.error || 'No se pudo cargar la configuración de roles y perfiles.',
        'error'
      );
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId, syncCurrentSessionIfNeeded]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Abrir modal para Nuevo Rol
  const handleOpenNewRole = () => {
    setRoleForm({
      id: null,
      nombre: '',
      descripcion: '',
      color: 'primary',
      es_admin: false,
      modulos: ['quoter', 'quote-viewer'],
      permisos_especiales: {
        l_enwhat: true,
      },
      sincronizar_operadores: true,
    });
    setShowRoleModal(true);
  };

  // Abrir modal para Editar Rol existente
  const handleOpenEditRole = (rol) => {
    setRoleForm({
      id: rol.id,
      nombre: rol.nombre || '',
      descripcion: rol.descripcion || '',
      color: rol.color || 'primary',
      es_admin: Boolean(rol.es_admin),
      modulos: Array.isArray(rol.modulos) ? [...rol.modulos] : [],
      permisos_especiales: {
        l_enwhat: Boolean(rol.permisos_especiales?.l_enwhat),
      },
      sincronizar_operadores: true,
    });
    setShowRoleModal(true);
  };

  const toggleRoleModulo = (modId) => {
    setRoleForm((prev) => {
      const exists = prev.modulos.includes(modId);
      const nextMods = exists ? prev.modulos.filter((m) => m !== modId) : [...prev.modulos, modId];
      return { ...prev, modulos: nextMods };
    });
  };

  const toggleRolePermiso = (permId) => {
    setRoleForm((prev) => ({
      ...prev,
      permisos_especiales: {
        ...prev.permisos_especiales,
        [permId]: !prev.permisos_especiales[permId],
      },
    }));
  };

  const handleSaveRole = async (e) => {
    if (e) e.preventDefault();
    if (!roleForm.nombre.trim()) {
      Swal.fire('Atención', 'Ingresá un nombre para el rol.', 'warning');
      return;
    }
    if (roleForm.modulos.length === 0) {
      Swal.fire('Atención', 'Seleccioná al menos un módulo habilitado para este rol.', 'warning');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post('/api/roles-perfiles/', {
        action: 'save_role',
        ...roleForm,
      });
      const data = response.data || {};
      setRoles(data.roles || []);
      setOperadores(data.operadores || []);
      syncCurrentSessionIfNeeded(data.operadores || []);
      setShowRoleModal(false);
      Swal.fire({
        icon: 'success',
        title: roleForm.id ? 'Rol actualizado' : 'Rol creado',
        text: `El perfil "${roleForm.nombre}" se guardó correctamente.`,
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire(
        'Error',
        error.response?.data?.error || 'No se pudo guardar el rol.',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (rol) => {
    if (rol.es_sistema) {
      Swal.fire(
        'Rol del Sistema',
        `El rol "${rol.nombre}" es un perfil base del sistema. Podés editar sus módulos y permisos, pero no eliminarlo.`,
        'info'
      );
      return;
    }

    const confirm = await Swal.fire({
      title: `¿Eliminar rol "${rol.nombre}"?`,
      text:
        rol.usuarios_count > 0
          ? `Hay ${rol.usuarios_count} usuario(s) con este rol que pasarán automáticamente al perfil Vendedor.`
          : 'Esta acción eliminará el rol de forma permanente.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      const response = await axios.post('/api/roles-perfiles/', {
        action: 'delete_role',
        id: rol.id,
      });
      const data = response.data || {};
      setRoles(data.roles || []);
      setOperadores(data.operadores || []);
      syncCurrentSessionIfNeeded(data.operadores || []);
      Swal.fire({
        icon: 'success',
        title: 'Rol eliminado',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire(
        'Error',
        error.response?.data?.error || 'No se pudo eliminar el rol.',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  // Abrir modal de configuración de Operador
  const handleOpenEditOperator = (op) => {
    setOpForm({
      c_usuar: op.c_usuar,
      d_nomus: op.d_nomus || '',
      d_apeus: op.d_apeus || '',
      d_email: op.d_email || '',
      rol_id: op.rol_id || (roles[0]?.id ?? 0),
      l_admin: Boolean(op.l_admin),
      l_enwhat: Boolean(op.l_enwhat),
      empresas_permitidas: Array.isArray(op.empresas_permitidas) ? [...op.empresas_permitidas] : [],
    });
    setShowOpModal(true);
  };

  const handleOperatorRoleSelectChange = (newRolId) => {
    const selectedRole = roles.find((r) => Number(r.id) === Number(newRolId));
    if (!selectedRole) {
      setOpForm((prev) => ({ ...prev, rol_id: Number(newRolId) }));
      return;
    }
    setOpForm((prev) => ({
      ...prev,
      rol_id: selectedRole.id,
      l_admin: Boolean(selectedRole.es_admin),
      l_enwhat: Boolean(selectedRole.permisos_especiales?.l_enwhat),
    }));
  };

  const toggleOperatorEmpresa = (nuges) => {
    const idNum = Number(nuges);
    setOpForm((prev) => {
      const exists = prev.empresas_permitidas.includes(idNum);
      const next = exists
        ? prev.empresas_permitidas.filter((x) => x !== idNum)
        : [...prev.empresas_permitidas, idNum];
      return { ...prev, empresas_permitidas: next };
    });
  };

  const handleSaveOperator = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    try {
      const response = await axios.post('/api/roles-perfiles/', {
        action: 'save_operator',
        ...opForm,
      });
      const data = response.data || {};
      setRoles(data.roles || []);
      setOperadores(data.operadores || []);
      syncCurrentSessionIfNeeded(data.operadores || []);
      setShowOpModal(false);
      Swal.fire({
        icon: 'success',
        title: 'Usuario actualizado',
        text: `Los permisos de ${opForm.c_usuar} se actualizaron correctamente.`,
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire(
        'Error',
        error.response?.data?.error || 'No se pudo actualizar el operador.',
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  // Cambio rápido de rol desde la tabla de operadores
  const handleQuickRoleChange = async (op, newRolId) => {
    const targetRole = roles.find((r) => Number(r.id) === Number(newRolId));
    if (!targetRole) return;

    setSaving(true);
    try {
      const response = await axios.post('/api/roles-perfiles/', {
        action: 'save_operator',
        c_usuar: op.c_usuar,
        rol_id: targetRole.id,
        l_admin: Boolean(targetRole.es_admin),
        l_enwhat: Boolean(targetRole.permisos_especiales?.l_enwhat),
      });
      const data = response.data || {};
      setRoles(data.roles || []);
      setOperadores(data.operadores || []);
      syncCurrentSessionIfNeeded(data.operadores || []);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: `${op.c_usuar} asignado a "${targetRole.nombre}"`,
        showConfirmButton: false,
        timer: 2000,
      });
    } catch (error) {
      Swal.fire('Error', 'No se pudo cambiar el rol del usuario.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Filtrado de operadores
  const filteredOperadores = operadores.filter((op) => {
    const q = searchOp.trim().toLowerCase();
    const matchesSearch =
      !q ||
      (op.c_usuar || '').toLowerCase().includes(q) ||
      (op.d_nomus || '').toLowerCase().includes(q) ||
      (op.d_apeus || '').toLowerCase().includes(q) ||
      (op.d_secto || '').toLowerCase().includes(q) ||
      (op.d_email || '').toLowerCase().includes(q) ||
      (op.rol || '').toLowerCase().includes(q);
    const matchesRol = !filterRolId || Number(op.rol_id) === Number(filterRolId);
    return matchesSearch && matchesRol;
  });

  const getModuleMeta = (modId) =>
    catalogoModulos.find((m) => m.id === modId) || {
      id: modId,
      nombre: modId,
      icon: 'bi-grid',
    };

  const adminCount = operadores.filter((o) => o.es_admin_efectivo).length;

  return (
    <div className="container-fluid py-4 px-3 px-md-4">
      {/* Encabezado Principal */}
      <div
        className="card border-0 shadow-sm mb-4 overflow-hidden"
        style={{
          borderRadius: '18px',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
        }}
      >
        <div className="card-body p-4 text-white">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-4 shadow"
                style={{
                  width: '58px',
                  height: '58px',
                  background: 'rgba(255,255,255,0.14)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                <i className="bi bi-shield-lock-fill fs-2 text-white"></i>
              </div>
              <div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <h3 className="fw-bold mb-0">Roles, Perfiles y Permisos</h3>
                  <span className="badge bg-light text-dark fw-semibold">
                    Seguridad SIGECOM
                  </span>
                </div>
                <p className="mb-0 text-white-50 small mt-1">
                  Definí qué módulos y acciones operativas están habilitados para cada perfil y asignalos a los operadores.
                </p>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-outline-light fw-semibold"
                style={{ borderRadius: '10px' }}
                onClick={fetchData}
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Actualizar
              </button>
              <button
                type="button"
                className="btn btn-light fw-bold text-primary shadow-sm"
                style={{ borderRadius: '10px' }}
                onClick={handleOpenNewRole}
              >
                <i className="bi bi-plus-circle-fill me-2"></i>
                Nuevo Rol / Perfil
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tarjetas de Resumen (KPIs) */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '14px' }}>
            <div className="card-body d-flex align-items-center gap-3">
              <div
                className="rounded-3 d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary"
                style={{ width: '48px', height: '48px' }}
              >
                <i className="bi bi-diagram-3-fill fs-4"></i>
              </div>
              <div>
                <div className="text-muted small fw-semibold">Roles Activos</div>
                <div className="fs-4 fw-bold text-dark">{roles.length}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '14px' }}>
            <div className="card-body d-flex align-items-center gap-3">
              <div
                className="rounded-3 d-flex align-items-center justify-content-center bg-success bg-opacity-10 text-success"
                style={{ width: '48px', height: '48px' }}
              >
                <i className="bi bi-people-fill fs-4"></i>
              </div>
              <div>
                <div className="text-muted small fw-semibold">Operadores (sope000)</div>
                <div className="fs-4 fw-bold text-dark">{operadores.length}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '14px' }}>
            <div className="card-body d-flex align-items-center gap-3">
              <div
                className="rounded-3 d-flex align-items-center justify-content-center bg-danger bg-opacity-10 text-danger"
                style={{ width: '48px', height: '48px' }}
              >
                <i className="bi bi-shield-check fs-4"></i>
              </div>
              <div>
                <div className="text-muted small fw-semibold">Nivel Administrador</div>
                <div className="fs-4 fw-bold text-dark">{adminCount}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '14px' }}>
            <div className="card-body d-flex align-items-center gap-3">
              <div
                className="rounded-3 d-flex align-items-center justify-content-center bg-warning bg-opacity-10 text-warning"
                style={{ width: '48px', height: '48px' }}
              >
                <i className="bi bi-grid-1x2-fill fs-4"></i>
              </div>
              <div>
                <div className="text-muted small fw-semibold">Módulos del Sistema</div>
                <div className="fs-4 fw-bold text-dark">{visibleModulos.length}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Selector de Pestañas */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <div
          className="d-inline-flex gap-1 p-1 bg-white border shadow-sm"
          style={{ borderRadius: '12px' }}
        >
          <button
            type="button"
            className={`btn px-4 py-2 fw-bold border-0 ${
              activeTab === 'roles' ? 'btn-primary shadow-sm' : 'btn-white bg-transparent text-secondary'
            }`}
            style={{ borderRadius: '9px' }}
            onClick={() => setActiveTab('roles')}
          >
            <i className="bi bi-shield-shaded me-2"></i>
            Perfiles y Roles ({roles.length})
          </button>
          <button
            type="button"
            className={`btn px-4 py-2 fw-bold border-0 ${
              activeTab === 'operadores' ? 'btn-primary shadow-sm' : 'btn-white bg-transparent text-secondary'
            }`}
            style={{ borderRadius: '9px' }}
            onClick={() => setActiveTab('operadores')}
          >
            <i className="bi bi-person-badge-fill me-2"></i>
            Usuarios / Operadores ({operadores.length})
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card border-0 shadow-sm p-5 text-center" style={{ borderRadius: '16px' }}>
          <div className="py-4">
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-3 mb-0 fw-semibold">
              Cargando matriz de roles, módulos y operadores...
            </p>
          </div>
        </div>
      ) : activeTab === 'roles' ? (
        /* ==================== PESTAÑA 1: ROLES Y PERFILES ==================== */
        <div className="row g-4">
          {roles.map((rol) => (
            <div className="col-12 col-lg-6 col-xl-4" key={rol.id}>
              <div
                className="card border-0 shadow-sm h-100 d-flex flex-column"
                style={{ borderRadius: '16px' }}
              >
                <div className="card-header bg-white border-bottom-0 pt-4 px-4 pb-0">
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <Badge bg={rol.color || 'primary'} className="px-3 py-2 fs-6 rounded-pill">
                          {rol.nombre}
                        </Badge>
                        {rol.es_admin && (
                          <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25">
                            <i className="bi bi-stars me-1"></i>Admin
                          </span>
                        )}
                        {rol.es_sistema ? (
                          <span className="badge bg-secondary bg-opacity-10 text-secondary">
                            Base del sistema
                          </span>
                        ) : (
                          <span className="badge bg-info bg-opacity-10 text-info">
                            Personalizado
                          </span>
                        )}
                      </div>
                      <p className="text-muted small mt-2 mb-0">
                        {rol.descripcion || 'Sin descripción adicional.'}
                      </p>
                    </div>

                    <div className="text-end">
                      <span
                        className="badge bg-light text-dark border px-2 py-1"
                        title="Operadores con este rol"
                      >
                        <i className="bi bi-people-fill me-1 text-primary"></i>
                        {rol.usuarios_count} {rol.usuarios_count === 1 ? 'usuario' : 'usuarios'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="card-body px-4 py-3 flex-grow-1">
                  {/* Módulos Habilitados */}
                  <div className="mb-3">
                    <div className="small fw-bold text-uppercase text-muted mb-2" style={{ fontSize: '0.73rem', letterSpacing: '0.04em' }}>
                      Módulos con Acceso ({rol.modulos?.filter(m => visibleModulos.some(vm => vm.id === m)).length || 0} de {visibleModulos.length})
                    </div>
                    <div className="d-flex flex-wrap gap-1">
                      {visibleModulos.map((mod) => {
                        const enabled = (rol.modulos || []).includes(mod.id);
                        return (
                          <span
                            key={mod.id}
                            className={`badge d-inline-flex align-items-center gap-1 px-2 py-1 ${
                              enabled
                                ? 'bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25'
                                : 'bg-light text-muted text-decoration-line-through opacity-50'
                            }`}
                            style={{ fontWeight: 500, fontSize: '0.78rem' }}
                          >
                            <i className={`bi ${enabled ? mod.icon : 'bi-lock'}`}></i>
                            {mod.nombre}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* Permisos Operativos Especiales */}
                  <div className="mb-3">
                    <div className="small fw-bold text-uppercase text-muted mb-2" style={{ fontSize: '0.73rem', letterSpacing: '0.04em' }}>
                      Permisos Operativos Especiales
                    </div>
                    <div className="d-flex flex-wrap gap-1">
                      {catalogoPermisos.map((perm) => {
                        const active = Boolean(rol.permisos_especiales?.[perm.id]);
                        return (
                          <span
                            key={perm.id}
                            className={`badge d-inline-flex align-items-center gap-1 px-2 py-1 ${
                              active
                                ? 'bg-success bg-opacity-10 text-success border border-success border-opacity-25'
                                : 'bg-light text-muted opacity-50'
                            }`}
                            style={{ fontWeight: 500, fontSize: '0.76rem' }}
                          >
                            <i className={`bi ${active ? 'bi-check-circle-fill' : 'bi-dash-circle'}`}></i>
                            {perm.nombre}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* Usuarios Asignados */}
                  <div>
                    <div className="small fw-bold text-uppercase text-muted mb-1" style={{ fontSize: '0.73rem', letterSpacing: '0.04em' }}>
                      Operadores asignados
                    </div>
                    {rol.usuarios && rol.usuarios.length > 0 ? (
                      <div className="d-flex flex-wrap gap-1">
                        {rol.usuarios.map((u) => (
                          <span
                            key={u.c_usuar}
                            className="badge bg-light text-dark border"
                            title={u.nombre}
                          >
                            <i className="bi bi-person-fill text-secondary me-1"></i>
                            <strong>{u.c_usuar}</strong>
                            {u.nombre ? ` · ${u.nombre}` : ''}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted small fst-italic">
                        Ningún operador asignado actualmente.
                      </span>
                    )}
                  </div>
                </div>

                <div className="card-footer bg-white border-top px-4 py-3 d-flex justify-content-between align-items-center" style={{ borderRadius: '0 0 16px 16px' }}>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary fw-semibold px-3"
                    style={{ borderRadius: '8px' }}
                    onClick={() => handleOpenEditRole(rol)}
                  >
                    <i className="bi bi-sliders me-1"></i>
                    Editar Permisos
                  </button>

                  {!rol.es_sistema && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      style={{ borderRadius: '8px' }}
                      onClick={() => handleDeleteRole(rol)}
                      title="Eliminar rol personalizado"
                    >
                      <i className="bi bi-trash3"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Card para crear un nuevo rol rápidamente */}
          <div className="col-12 col-lg-6 col-xl-4">
            <div
              className="card border-2 h-100 d-flex align-items-center justify-content-center text-center p-4"
              style={{
                borderRadius: '16px',
                borderStyle: 'dashed',
                borderColor: '#cbd5e1',
                background: '#f8fafc',
                cursor: 'pointer',
                minHeight: '280px',
              }}
              onClick={handleOpenNewRole}
            >
              <div>
                <div
                  className="rounded-circle bg-primary bg-opacity-10 text-primary d-inline-flex align-items-center justify-content-center mb-3"
                  style={{ width: '60px', height: '60px' }}
                >
                  <i className="bi bi-plus-lg fs-3"></i>
                </div>
                <h5 className="fw-bold text-dark mb-1">Crear Nuevo Rol / Perfil</h5>
                <p className="text-muted small mb-0 px-3">
                  Definí un nuevo perfil (ej. Encargado de Catálogo, Auditor, Mostrador) y elegí a qué módulos puede ingresar.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* ==================== PESTAÑA 2: OPERADORES / USUARIOS ==================== */
        <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
          <div className="card-header bg-white border-bottom p-3 p-md-4" style={{ borderRadius: '16px 16px 0 0' }}>
            <div className="row g-3 align-items-center">
              <div className="col-12 col-md-6">
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0">
                    <i className="bi bi-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control bg-light border-start-0"
                    placeholder="Buscar operador por código (ej. ALI, BLA), nombre, apellido o email..."
                    value={searchOp}
                    onChange={(e) => setSearchOp(e.target.value)}
                  />
                  {searchOp && (
                    <button
                      type="button"
                      className="btn btn-light border"
                      onClick={() => setSearchOp('')}
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
              </div>

              <div className="col-12 col-md-4">
                <select
                  className="form-select bg-light"
                  value={filterRolId}
                  onChange={(e) => setFilterRolId(e.target.value)}
                >
                  <option value="">Todos los roles ({operadores.length})</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      Rol: {r.nombre} ({r.usuarios_count})
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-2 text-md-end">
                <span className="text-muted small fw-semibold">
                  Mostrando {filteredOperadores.length} de {operadores.length}
                </span>
              </div>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-4">Operador</th>
                  <th>Rol / Perfil Asignado</th>
                  <th>Módulos Habilitados</th>
                  <th>Permisos Especiales</th>
                  <th>Empresas Habilitadas</th>
                  <th className="text-end pe-4">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredOperadores.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5 text-muted">
                      No se encontraron operadores con ese criterio de búsqueda.
                    </td>
                  </tr>
                ) : (
                  filteredOperadores.map((op) => (
                    <tr key={op.c_usuar}>
                      <td className="ps-4">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle bg-primary bg-opacity-10 text-primary fw-bold d-flex align-items-center justify-content-center"
                            style={{ width: '38px', height: '38px', fontSize: '0.82rem' }}
                          >
                            {op.c_usuar}
                          </div>
                          <div>
                            <div className="fw-bold text-dark">
                              {op.d_nomus} {op.d_apeus}
                              {user?.c_usuar === op.c_usuar && (
                                <span className="badge bg-primary ms-2" style={{ fontSize: '0.68rem' }}>
                                  Vos
                                </span>
                              )}
                            </div>
                            <div className="text-muted small">
                              Cód: <strong>{op.c_usuar}</strong>
                              {op.c_vende ? ` · Vend. #${op.c_vende}` : ''}
                              {op.d_email ? ` · ${op.d_email}` : ''}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td style={{ minWidth: '190px' }}>
                        <div className="d-flex align-items-center gap-2">
                          <select
                            className="form-select form-select-sm fw-semibold"
                            style={{ maxWidth: '175px', borderRadius: '8px' }}
                            value={op.rol_id || ''}
                            disabled={saving}
                            onChange={(e) => handleQuickRoleChange(op, e.target.value)}
                          >
                            {roles.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.nombre}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>

                      <td>
                        <div className="d-flex flex-wrap gap-1" style={{ maxWidth: '280px' }}>
                          {(op.modulos_permitidos || []).filter(mId => visibleModulos.some(vm => vm.id === mId)).map((mId) => {
                            const meta = getModuleMeta(mId);
                            return (
                              <span
                                key={mId}
                                className="badge bg-light text-dark border"
                                style={{ fontWeight: 500, fontSize: '0.73rem' }}
                              >
                                <i className={`bi ${meta.icon} text-primary me-1`}></i>
                                {meta.nombre}
                              </span>
                            );
                          })}
                        </div>
                      </td>

                      <td>
                        <div className="d-flex flex-wrap gap-1" style={{ maxWidth: '240px' }}>
                          {catalogoPermisos.map((p) => {
                            const hasPerm = Boolean(op.permisos_efectivos?.[p.id]);
                            if (!hasPerm) return null;
                            return (
                              <span
                                key={p.id}
                                className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25"
                                style={{ fontSize: '0.72rem', fontWeight: 500 }}
                              >
                                {p.nombre}
                              </span>
                            );
                          })}
                          {!catalogoPermisos.some((p) => op.permisos_efectivos?.[p.id]) && (
                            <span className="text-muted small">Estándar</span>
                          )}
                        </div>
                      </td>

                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {(op.empresas_permitidas || []).length > 0 ? (
                            op.empresas_permitidas.map((nuges) => {
                              const empObj = empresas.find((e) => Number(e.c_nuges) === Number(nuges));
                              return (
                                <span
                                  key={nuges}
                                  className="badge bg-secondary bg-opacity-10 text-dark border"
                                  style={{ fontSize: '0.73rem' }}
                                >
                                  <i className="bi bi-building me-1 text-secondary"></i>
                                  {empObj ? empObj.nombre : `Emp. ${nuges}`}
                                </span>
                              );
                            })
                          ) : (
                            <span className="badge bg-warning bg-opacity-10 text-warning border border-warning">
                              Sin empresas
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="text-end pe-4">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary fw-semibold"
                          style={{ borderRadius: '8px' }}
                          onClick={() => handleOpenEditOperator(op)}
                        >
                          <i className="bi bi-gear-fill me-1"></i>
                          Configurar
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ==================== MODAL CREAR / EDITAR ROL ==================== */}
      <Modal
        show={showRoleModal}
        onHide={() => setShowRoleModal(false)}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="fw-bold">
            <i className="bi bi-shield-lock-fill text-primary me-2"></i>
            {roleForm.id ? `Editar Rol: ${roleForm.nombre}` : 'Crear Nuevo Rol / Perfil'}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSaveRole}>
          <Modal.Body className="pt-3">
            <div className="row g-3 mb-3">
              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold small">Nombre del Rol *</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ej: Encargado de Catálogo, Supervisor Ventas..."
                  value={roleForm.nombre}
                  onChange={(e) => setRoleForm({ ...roleForm, nombre: e.target.value })}
                  required
                />
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label fw-semibold small">Color de Identificación</label>
                <select
                  className="form-select"
                  value={roleForm.color}
                  onChange={(e) => setRoleForm({ ...roleForm, color: e.target.value })}
                >
                  {COLOR_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12">
                <label className="form-label fw-semibold small">Descripción del Perfil</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Breve descripción del alcance de este perfil..."
                  value={roleForm.descripcion}
                  onChange={(e) => setRoleForm({ ...roleForm, descripcion: e.target.value })}
                />
              </div>
            </div>

            {/* Selección de Módulos del Sistema */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label fw-bold mb-0">
                  <i className="bi bi-grid-1x2-fill text-primary me-2"></i>
                  Módulos Habilitados en el Menú Principal
                </label>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 text-decoration-none"
                    onClick={() =>
                      setRoleForm((prev) => ({
                        ...prev,
                        modulos: visibleModulos.map((m) => m.id),
                      }))
                    }
                  >
                    Marcar todos
                  </button>
                  <span className="text-muted">·</span>
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 text-decoration-none text-secondary"
                    onClick={() =>
                      setRoleForm((prev) => ({
                        ...prev,
                        modulos: ['quoter'],
                      }))
                    }
                  >
                    Solo Cotizador
                  </button>
                </div>
              </div>

              {!isWebEnabled && (
                <div className="alert alert-light border py-2 px-3 mb-2 rounded-3 small text-muted d-flex align-items-center gap-2">
                  <i className="bi bi-info-circle text-primary"></i>
                  <span>Los módulos de Tienda Online y Web Unificada están ocultos porque la empresa activa no tiene el sitio web habilitado en <code>sges000</code>.</span>
                </div>
              )}
              <div className="row g-2">
                {visibleModulos.map((mod) => {
                  const checked = roleForm.modulos.includes(mod.id);
                  return (
                    <div className="col-12 col-md-6" key={mod.id}>
                      <div
                        className={`p-3 rounded-3 border d-flex align-items-start gap-3 ${
                          checked ? 'border-primary bg-primary bg-opacity-10' : 'bg-white'
                        }`}
                        style={{ cursor: 'pointer', transition: 'all 0.15s ease' }}
                        onClick={() => toggleRoleModulo(mod.id)}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input mt-1"
                          checked={checked}
                          onChange={() => {}}
                        />
                        <div>
                          <div className="fw-bold text-dark d-flex align-items-center gap-2">
                            <i className={`bi ${mod.icon} text-primary`}></i>
                            {mod.nombre}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                            {mod.descripcion}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Permisos Operativos Especiales */}
            <div className="mb-3">
              <label className="form-label fw-bold mb-2">
                <i className="bi bi-sliders text-success me-2"></i>
                Permisos Operativos Especiales
              </label>
              <div className="row g-2">
                {catalogoPermisos.map((perm) => {
                  const checked = Boolean(roleForm.permisos_especiales?.[perm.id]);
                  return (
                    <div className="col-12 col-md-6" key={perm.id}>
                      <div
                        className={`p-2 px-3 rounded-3 border d-flex align-items-center justify-content-between ${
                          checked ? 'border-success bg-success bg-opacity-10' : 'bg-white'
                        }`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleRolePermiso(perm.id)}
                      >
                        <div>
                          <div className="fw-semibold text-dark small">{perm.nombre}</div>
                          <div className="text-muted" style={{ fontSize: '0.74rem' }}>
                            {perm.descripcion}
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          className="form-check-input ms-2"
                          checked={checked}
                          onChange={() => {}}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {roleForm.id && (
              <div className="form-check mt-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="syncOpsCheck"
                  checked={roleForm.sincronizar_operadores}
                  onChange={(e) =>
                    setRoleForm({ ...roleForm, sincronizar_operadores: e.target.checked })
                  }
                />
                <label className="form-check-label small text-muted" htmlFor="syncOpsCheck">
                  Actualizar automáticamente los permisos de todos los usuarios asignados a este rol en <code>sope000</code>.
                </label>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="border-top-0">
            <Button variant="light" onClick={() => setShowRoleModal(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" className="fw-bold px-4" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar Rol y Permisos'}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* ==================== MODAL CONFIGURAR OPERADOR ==================== */}
      <Modal
        show={showOpModal}
        onHide={() => setShowOpModal(false)}
        size="lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="fw-bold">
            <i className="bi bi-person-gear text-primary me-2"></i>
            Configurar Operador: {opForm.c_usuar}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSaveOperator}>
          <Modal.Body className="pt-3">
            <div className="row g-3 mb-3">
              <div className="col-12 col-md-4">
                <label className="form-label fw-semibold small">Nombre</label>
                <input
                  type="text"
                  className="form-control"
                  value={opForm.d_nomus}
                  onChange={(e) => setOpForm({ ...opForm, d_nomus: e.target.value })}
                />
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label fw-semibold small">Apellido</label>
                <input
                  type="text"
                  className="form-control"
                  value={opForm.d_apeus}
                  onChange={(e) => setOpForm({ ...opForm, d_apeus: e.target.value })}
                />
              </div>
              <div className="col-12 col-md-4">
                <label className="form-label fw-semibold small">Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={opForm.d_email}
                  onChange={(e) => setOpForm({ ...opForm, d_email: e.target.value })}
                />
              </div>
            </div>

            {/* Selección de Rol */}
            <div className="p-3 rounded-3 bg-light border mb-3">
              <label className="form-label fw-bold mb-1">
                <i className="bi bi-shield-shaded text-primary me-2"></i>
                Rol / Perfil Asignado
              </label>
              <p className="text-muted small mb-2">
                Al cambiar el rol, se aplican por defecto sus módulos y permisos operativos.
              </p>
              <select
                className="form-select fw-semibold"
                value={opForm.rol_id}
                onChange={(e) => handleOperatorRoleSelectChange(e.target.value)}
              >
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nombre} — ({r.modulos?.length || 0} módulos habilitados)
                  </option>
                ))}
              </select>
            </div>

            {/* Permisos individuales en sope000 */}
            <div className="mb-3">
              <label className="form-label fw-bold mb-2">
                <i className="bi bi-toggles text-success me-2"></i>
                Permisos Operativos
              </label>
              <div className="row g-2">
                {catalogoPermisos.map((perm) => (
                  <div className="col-12" key={perm.id}>
                    <div className="p-2 px-3 rounded-3 border d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-semibold small">{perm.nombre}</div>
                        <div className="text-muted" style={{ fontSize: '0.73rem' }}>
                          {perm.descripcion}
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={Boolean(opForm[perm.id])}
                        onChange={(e) =>
                          setOpForm({ ...opForm, [perm.id]: e.target.checked })
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Empresas habilitadas (sope010) */}
            <div>
              <label className="form-label fw-bold mb-2">
                <i className="bi bi-building-check text-primary me-2"></i>
                Empresas Habilitadas para Operar (<code>sope010</code>)
              </label>
              <div className="row g-2">
                {empresas.map((emp) => {
                  const checked = opForm.empresas_permitidas.includes(Number(emp.c_nuges));
                  return (
                    <div className="col-12 col-md-6" key={emp.c_nuges}>
                      <div
                        className={`p-2 px-3 rounded-3 border d-flex justify-content-between align-items-center ${
                          checked ? 'border-primary bg-primary bg-opacity-10' : 'bg-white'
                        }`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleOperatorEmpresa(emp.c_nuges)}
                      >
                        <div>
                          <div className="fw-bold small text-dark">{emp.nombre}</div>
                          <div className="text-muted" style={{ fontSize: '0.73rem' }}>
                            Empresa #{emp.c_nuges} · Base: {emp.db_name}
                          </div>
                        </div>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={checked}
                          onChange={() => {}}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-top-0">
            <Button variant="light" onClick={() => setShowOpModal(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" className="fw-bold px-4" disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar Cambios del Operador'}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default RolesProfilesAdmin;
